import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2 } from 'lucide-react';
import AdminHeader from '../../../components/admin/common/AdminHeader';
import AdminTable from '../../../components/admin/common/AdminTable';
import AdminSearch from '../../../components/admin/common/AdminSearch';
import StatusBadge from '../../../components/admin/common/StatusBadge';
import AdminBreadcrumb from '../../../components/admin/common/AdminBreadcrumb';
import DeleteConfirmModal from '../../../components/admin/common/DeleteConfirmModal';
import { formatPrice } from '../../../utils/helpers';
import type { FirebaseProduct, Product } from '../../../types/product';
import { productService } from '../../../services/productService';
import StockEditor from '../../../components/admin/common/StockEditor';

const ProductsList: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const products = await productService.getProducts();
        setProducts(products);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;

    const searchLower = searchTerm.toLowerCase();
    return products.filter(
      (product) =>
        product.name.toLowerCase().includes(searchLower) ||
        product.sku.toLowerCase().includes(searchLower) ||
        product.brand.toLowerCase().includes(searchLower) ||
        product.category.toLowerCase().includes(searchLower) ||
        product.description.toLowerCase().includes(searchLower)
    );
  }, [products, searchTerm]);

  const handleStockUpdate = async (productId: string, newStock: number) => {
    try {
      await productService.updateProduct(productId, { stock: newStock });
      // Update the local products state to reflect the change
      setProducts(products.map(product => 
        product.id === productId 
          ? { ...product, stock: newStock }
          : product
      ));
    } catch (error) {
      console.error('Failed to update stock:', error);
      throw error;
    }
  };

  const handleDelete = async () => {
    if (!selectedProduct) return;

    try {
      await productService.deleteProduct(selectedProduct.id);
      setProducts((prev) => prev.filter((p) => p.id !== selectedProduct.id));
      setDeleteModalOpen(false);
      setSelectedProduct(null);
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const columns = [
    {
      key: 'name',
      title: 'Product',
      render: (product: Product) => (
        <div>
          <Link
            to={`/admin/products/${product.id}/edit`}
            className="font-medium text-gray-900 hover:text-brand-blue-500"
          >
            {product.name}
          </Link>
          <div className="text-sm text-gray-500">SKU: {product.sku}</div>
          <div className="text-sm text-gray-500">Brand: {product.brand}</div>
        </div>
      ),
    },
    {
      key: 'category',
      title: 'Category',
      render: (product: Product) => (
        <div>
          <div className="font-medium">{product.category}</div>
          {product.specifications && (
            <div className="text-sm text-gray-500">
              {Object.entries(product.specifications)
                .slice(0, 2)
                .map(([key, value]) => (
                  <div key={key}>
                    {key}: {value}
                  </div>
                ))}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'price',
      title: 'Price',
      render: (product: Product) => (
        <div>
          <div className="font-medium">{formatPrice(product.price)}</div>
          {product.rating && (
            <div className="text-sm text-gray-500">
              Rating: {product.rating} ({product.reviewCount} reviews)
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'stock',
      title: 'Stock',
      render: (product: FirebaseProduct) => (
        <div onClick={(e) => e.stopPropagation()}>
          <StockEditor
            productId={product.id}
            currentStock={product.stock}
            onUpdateStock={(newStock) => handleStockUpdate(product.id, newStock)}
          />
        </div>
      )
    },
    {
      key: 'status',
      title: 'Status',
      render: (product: Product) => (
        <div>
          <StatusBadge
            status={product.featured ? 'active' : 'inactive'}
            text={product.featured ? 'Featured' : 'Not Featured'}
            size="sm"
          />
          <div className="mt-1">
            <StatusBadge status={product.stock > 0 ? 'active' : 'inactive'} size="sm" />
          </div>
        </div>
      ),
    },
    {
      key: 'actions',
      title: '',
      render: (product: Product) => (
        <div className="flex items-center space-x-2">
          <Link
            to={`/admin/products/${product.id}/edit`}
            className="p-2 text-gray-400 hover:text-brand-blue-500 transition-colors"
          >
            <Edit className="w-5 h-5" />
          </Link>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedProduct(product);
              setDeleteModalOpen(true);
            }}
            className="p-2 text-gray-400 hover:text-red-500 transition-colors"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <AdminBreadcrumb items={[{ label: 'Products', href: '/admin/products' }]} />

      <AdminHeader
        title={`Products (${filteredProducts.length})`}
        actions={
          <div className="flex items-center space-x-4">
            <div className="w-64">
              <AdminSearch placeholder="Search products..." onSearch={setSearchTerm} />
            </div>
            <Link
              to="/admin/products/create"
              className="inline-flex items-center px-4 py-2 bg-brand-blue-500 text-white rounded-lg hover:bg-brand-blue-600 transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Product
            </Link>
          </div>
        }
      />

      <div className="bg-white rounded-lg shadow-sm">
        <AdminTable
          columns={columns}
          data={filteredProducts}
          isLoading={isLoading}
          onRowClick={(product) => (window.location.href = `/admin/products/${product.id}/edit`)}
        />
      </div>

      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedProduct(null);
        }}
        onConfirm={handleDelete}
        title="Delete Product"
        message="Are you sure you want to delete this product? This action cannot be undone."
        itemName={selectedProduct?.name}
      />
    </div>
  );
};

export default ProductsList;